import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, MapPin, Car, Clock, Instagram, Youtube } from 'lucide-react';
import { STUDIO, SCHEDULE, WHY_PITFIT, PRESS, SOCIALS, VIDEOS } from '../data/mock';
import { usePageMeta } from '../components/Layout';
import { Reveal, SectionHeader, ProofStrip, ClassCard, PricingTiers, ReviewWall, CloseCTA, ICONS, PitVideo, VideoSeo } from '../components/Shared';
import { useSchedule } from '../lib/api';

function todayIndex() {
  const d = new Date().getDay(); // 0 = Sunday
  return d === 0 ? 6 : d - 1;
}

function HomeHero() {
  return (
    <section className="relative overflow-hidden min-h-[86vh] flex items-center">
      <PitVideo
        video={VIDEOS.hero}
        className="absolute inset-0 w-full h-full object-cover"
        label="Background video of members training in a PITFIT HIIT class"
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 w-full">
        <Reveal>
          <p className="mono-label mb-5">Travis Walk · Uptown Dallas · Est. {STUDIO.founded}</p>
          <h1 className="h-display text-white max-w-3xl">
            HIIT Workout Studio in <span style={{ color: 'var(--pf-brand2)' }}>Uptown Dallas</span>
          </h1>
          <p className="text-[#c9d1e0] text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
            Non-stop, coached high-intensity training inside a Travis Walk parking garage — 45- and 60-minute classes sized so you never hide, never coast, never plateau.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-9">
            <Link to="/book" className="btn-primary text-[17px] !px-8">Book a Class <ArrowRight size={18} /></Link>
            <Link to="/hiit-workout/schedule" className="btn-ghost">View Schedule</Link>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 font-mono-pf text-[12px] uppercase tracking-[0.12em] text-[#aeb8cc]">
            <span className="flex items-center gap-1.5"><Star size={13} fill="currentColor" style={{ color: 'var(--pf-brand2)' }} /> {STUDIO.rating} · {STUDIO.reviewCount}+ reviews</span>
            <span aria-hidden="true">/</span>
            <span>As seen in D Magazine</span>
            <span aria-hidden="true">/</span>
            <span>$15 first class</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ConceptSection() {
  return (
    <section className="section-pad">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="mono-label mb-4">The Concept</p>
          <h2 className="h-section text-white">45 or 60 minutes. Non-stop. Nothing wasted.</h2>
          <p className="text-muted-pf mt-5 text-[17px] leading-relaxed">
            Every PITFIT class is programmed high-intensity intervals — rotating between strength blocks, conditioning circuits, and sculpt work. The weekly split hits upper body, lower body, full body, and a burnout day, so you can come daily without wrecking recovery.
          </p>
          <p className="text-muted-pf mt-4 text-[17px] leading-relaxed">
            It happens in a concrete pit — a converted parking garage at Travis Walk. Raw, loud, and unlike any polished studio in Dallas. You'll burn 500–900 calories and you'll know exactly where the name comes from.
          </p>
          <div className="flex gap-3 mt-7">
            <Link to="/the-workout" className="btn-ghost">Explore The Workout <ArrowRight size={16} /></Link>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="relative">
            <div className="rounded-2xl w-full h-[420px] overflow-hidden border border-[rgba(148,170,220,0.16)]">
              <PitVideo video={VIDEOS.classes} className="w-full h-full object-cover" label="Inline video showcasing PITFIT class training blocks" />
            </div>
            <div className="absolute -bottom-5 -left-3 sm:-left-6 glass-card px-5 py-4" style={{ borderColor: 'rgba(125,167,255,0.4)' }}>
              <p className="font-mono-pf text-[11px] uppercase tracking-[0.14em] text-muted-pf">Calories / class</p>
              <p className="font-display font-bold text-2xl text-white">500–900</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TodayClasses({ today }) {
  return (
    <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="mono-label mb-4">Live Schedule</p>
            <h2 className="h-section text-white">Today in the PIT · {today.day}</h2>
          </div>
          <Link to="/hiit-workout/schedule" className="btn-quiet">Full weekly schedule <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {today.classes.map((cls, i) => (
            <Reveal key={`${cls.time}-${cls.name}-${cls.coach}`} delay={i * 80}>
              <ClassCard cls={cls} day={today.day} />
            </Reveal>
          ))}
        </div>
        <p className="font-mono-pf text-[12px] uppercase tracking-[0.12em] text-muted-pf mt-6">Live availability on the booking board · classes fill fast</p>
      </div>
    </section>
  );
}

function HyroxTeaser() {
  return (
    <section className="section-pad !py-14">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal>
          <div className="glass-card relative overflow-hidden p-8 md:p-10 grid lg:grid-cols-[1fr_auto] items-center gap-6" style={{ borderColor: 'rgba(125,167,255,0.35)' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 0% 50%, rgba(70,127,247,0.12), transparent 55%)' }} />
            <div className="relative">
              <p className="mono-label mb-3">New · Race-Specific Training</p>
              <h2 className="h-section text-white">HYROX classes, 6 days a week.</h2>
              <p className="text-muted-pf mt-3 text-[16.5px] max-w-2xl leading-relaxed">
                Training for HYROX Dallas 2026? Sleds, sandbag lunges, wall balls and compromised running — coached by Evan Conrad, Sara Yingling and founder Chris Wiese. $15 first class.
              </p>
            </div>
            <Link to="/hyrox-classes-dallas" className="btn-primary relative shrink-0">Explore HYROX Training <ArrowRight size={16} /></Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function WhyPitfitSection() {
  return (
    <section className="section-pad">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader tag="Why PITFIT" title="Built different. Literally." sub="Six reasons a parking garage in Uptown outworks every polished gym floor in Dallas." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHY_PITFIT.map((w, i) => {
            const Icon = ICONS[w.icon];
            return (
              <Reveal key={w.title} delay={i * 70}>
                <div className="glass-card glass-card-hover p-7 h-full">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(70,127,247,0.12)', border: '1px solid rgba(125,167,255,0.3)' }}>
                    <Icon size={20} style={{ color: 'var(--pf-brand2)' }} />
                  </div>
                  <h3 className="font-display font-semibold text-white text-lg">{w.title}</h3>
                  <p className="text-muted-pf text-[15px] mt-2.5 leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function GarageStory() {
  return (
    <section className="relative overflow-hidden">
      <PitVideo video={VIDEOS.crew} className="absolute inset-0 w-full h-full object-cover" label="The PITFIT crew training in the Travis Walk parking garage" />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(6,8,13,0.94) 0%, rgba(6,8,13,0.72) 55%, rgba(6,8,13,0.45) 100%)' }} />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 md:py-32">
        <Reveal>
          <p className="mono-label mb-4">The Parking Garage Story</p>
          <h2 className="h-section text-white max-w-xl">You park at your workout.</h2>
          <p className="text-[#c3cbdc] mt-5 text-[17px] max-w-xl leading-relaxed">
            In 2020, veteran Dallas coach Chris Wiese looked at a concrete deck in the Travis Walk garage and saw a gym Dallas didn't have: raw, loud, honest. Five years later, the PIT has a cult following, a D Magazine feature, and a 6 AM crew that shows up in every season.
          </p>
          <Link to="/about" className="btn-ghost mt-8">Read Our Story <ArrowRight size={16} /></Link>
        </Reveal>
      </div>
    </section>
  );
}

function ReviewsSection() {
  return (
    <section className="section-pad">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader tag="Social Proof" title={`${STUDIO.rating}★ from ${STUDIO.reviewCount}+ real members`} sub="Pulled from Google, Yelp and ClassPass. Unedited, unfiltered, unpaid." center />
        <ReviewWall />
        <div className="text-center mt-10">
          <Link to="/results" className="btn-quiet">See member results <ArrowRight size={15} /></Link>
        </div>
      </div>
    </section>
  );
}

function PricingSnapshot() {
  return (
    <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader tag="Pricing" title="Simple, transparent, no surprises." sub="Start with a $15 first class — then pick whatever fits your week. No hidden fees, no fine print." center />
        <PricingTiers compact />
        <div className="text-center mt-10">
          <Link to="/pricing" className="btn-quiet">Full pricing details <ArrowRight size={15} /></Link>
        </div>
      </div>
    </section>
  );
}

function FromThePit() {
  return (
    <section className="section-pad">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div className="max-w-2xl">
            <p className="mono-label mb-4">From The PIT</p>
            <h2 className="h-section text-white">Watch it. Then feel it.</h2>
            <p className="text-muted-pf mt-4 text-[17px] leading-relaxed">Cameras don't fully capture the energy in the garage — but they get close. See the PIT in action, then follow along on Instagram.</p>
          </div>
          <div className="flex gap-3">
            <a href={SOCIALS.instagram} target="_blank" rel="noreferrer" className="btn-ghost !min-h-[44px] !py-2 text-[14px]"><Instagram size={16} /> @pitfitworkout</a>
            <a href={SOCIALS.youtube} target="_blank" rel="noreferrer" className="btn-ghost !min-h-[44px] !py-2 text-[14px]"><Youtube size={16} /> YouTube</a>
          </div>
        </div>
        <div className="grid lg:grid-cols-5 gap-6 items-stretch">
          <Reveal className="lg:col-span-3">
            <div className="glass-card overflow-hidden h-full flex flex-col">
              <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${SOCIALS.featuredVideo.id}`}
                  title={SOCIALS.featuredVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <p className="mono-label">Member POV</p>
                <h3 className="font-display font-semibold text-white text-lg mt-2">{SOCIALS.featuredVideo.title}</h3>
                <p className="text-muted-pf text-[14.5px] mt-2">{SOCIALS.featuredVideo.desc}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-2">
            <div className="glass-card overflow-hidden h-full flex flex-col">
              <div className="flex-1 min-h-[420px]">
                <iframe
                  src={SOCIALS.instagramEmbed}
                  title="PITFIT on Instagram — @pitfitworkout latest posts"
                  className="w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  loading="lazy"
                  style={{ background: '#fff', minHeight: 420 }}
                />
              </div>
              <a href={SOCIALS.instagram} target="_blank" rel="noreferrer" className="p-5 flex items-center justify-between border-t border-[rgba(148,170,220,0.13)] group">
                <span className="font-display font-semibold text-white text-[15px] flex items-center gap-2.5"><Instagram size={17} style={{ color: 'var(--pf-brand2)' }} /> Follow @pitfitworkout</span>
                <ArrowRight size={16} className="text-muted-pf group-hover:text-white transition-colors" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function PressSection() {
  return (
    <section className="section-pad">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader tag="As Seen In" title="Dallas noticed." center />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PRESS.map((p, i) => (
            <Reveal key={p.outlet} delay={i * 70}>
              <div className="glass-card glass-card-hover p-6 h-full flex flex-col">
                <span className="mono-label">{p.tag}</span>
                <h3 className="font-display font-bold text-white text-xl mt-3">{p.outlet}</h3>
                <p className="text-muted-pf text-[14px] mt-3 leading-relaxed flex-1">“{p.quote}”</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocationSection() {
  return (
    <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <p className="mono-label mb-4">Find The PIT</p>
          <h2 className="h-section text-white">Travis Walk, Knox Street District.</h2>
          <ul className="mt-7 space-y-5">
            <li className="flex gap-4">
              <MapPin size={20} className="shrink-0 mt-1" style={{ color: 'var(--pf-brand)' }} />
              <div>
                <p className="text-white font-medium">{STUDIO.address}</p>
                <p className="text-muted-pf text-[14px] mt-0.5">{STUDIO.neighborhood}</p>
              </div>
            </li>
            <li className="flex gap-4">
              <Car size={20} className="shrink-0 mt-1" style={{ color: 'var(--pf-brand)' }} />
              <div>
                <p className="text-white font-medium">Free parking in the garage</p>
                <p className="text-muted-pf text-[14px] mt-0.5">Enter Travis Walk Plaza Parking Garage on Travis Street, proceed to the lower 3rd level, and park in any open spot. You literally park at your workout.</p>
              </div>
            </li>
            <li className="flex gap-4">
              <Clock size={20} className="shrink-0 mt-1" style={{ color: 'var(--pf-brand)' }} />
              <div>
                {STUDIO.hours.map((h) => (
                  <p key={h.d} className="text-[15px]"><span className="text-white font-medium">{h.d}:</span> <span className="text-muted-pf">{h.h}</span></p>
                ))}
              </div>
            </li>
          </ul>
          <div className="flex flex-wrap gap-2.5 mt-7">
            {['Uptown', 'Knox-Henderson', 'Highland Park', 'Park Cities'].map((n) => (
              <Link key={n} to={n === 'Uptown' ? '/locations/uptown-dallas' : `/hiit-classes-dallas/${n.toLowerCase().replace(' ', '-')}`} className="font-mono-pf text-[12px] uppercase tracking-[0.12em] px-4 py-2 rounded-full border border-[rgba(148,170,220,0.25)] text-muted-pf hover:text-white hover:border-[rgba(125,167,255,0.5)] transition-colors">
                {n}
              </Link>
            ))}
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="rounded-2xl overflow-hidden border border-[rgba(148,170,220,0.16)] h-[380px]">
            <iframe
              title="PITFIT location — 4514 Travis St, Dallas, TX"
              src={STUDIO.mapEmbed}
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) contrast(0.9)' }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  usePageMeta(
    null,
    'PITFIT is a coached, non-stop HIIT workout studio in Uptown Dallas at Travis Walk. 45- and 60-minute classes, HIIT and HYROX programs. $15 first class for new clients.'
  );

  const { schedule } = useSchedule();
  const week = schedule || SCHEDULE;
  const today = week[todayIndex()];

  return (
    <>
      <VideoSeo keys={['hero', 'classes', 'crew']} />
      <HomeHero />
      <ProofStrip />
      <ConceptSection />
      <TodayClasses today={today} />
      <HyroxTeaser />
      <WhyPitfitSection />
      <GarageStory />
      <ReviewsSection />
      <PricingSnapshot />
      <FromThePit />
      <PressSection />
      <LocationSection />
      <CloseCTA />
    </>
  );
}
